/** Content reports: users flag an item so staff can fix it. Emails are built here, sent by the API. */

export const REPORT_REASONS = [
  "incorrect_info",
  "safety_concern",
  "typo",
  "missing_info",
  "offensive",
  "other",
] as const;
export type ReportReason = (typeof REPORT_REASONS)[number];

export const REPORT_REASON_LABELS: Record<ReportReason, string> = {
  incorrect_info: "Incorrect information",
  safety_concern: "Safety concern",
  typo: "Typo or formatting",
  missing_info: "Missing information",
  offensive: "Offensive or disrespectful",
  other: "Something else",
};

export const REPORT_STATUSES = ["open", "resolved", "dismissed"] as const;
export type ReportStatus = (typeof REPORT_STATUSES)[number];

export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export interface ReportEmailInput {
  reportId: string;
  reason: ReportReason;
  message: string;
  item: { category: string; slug: string; name: string };
  reporter: { email: string | null; name: string | null } | null;
  adminUrl: string;
}

/** Safety concerns are prioritised in the subject line so they're triaged first. */
export function buildReportEmail(input: ReportEmailInput): {
  subject: string;
  text: string;
  html: string;
} {
  const label = REPORT_REASON_LABELS[input.reason];
  const urgent = input.reason === "safety_concern" ? "[URGENT] " : "";
  const subject = `${urgent}Hexling report: ${label} on ${input.item.name}`;
  const who = input.reporter
    ? `${input.reporter.name ?? "Unnamed witch"} <${input.reporter.email ?? "no email"}>`
    : "Anonymous (signed out)";
  const link = `${input.adminUrl.replace(/\/$/, "")}/admin/reports/${input.reportId}`;
  const lines = [
    `Item: ${input.item.name} (${input.item.category}/${input.item.slug})`,
    `Reason: ${label}`,
    `From: ${who}`,
    "",
    input.message,
    "",
    `Review: ${link}`,
  ];
  const html = [
    `<h2>${escapeHtml(subject)}</h2>`,
    `<p><strong>Item:</strong> ${escapeHtml(input.item.name)} (${escapeHtml(input.item.category)}/${escapeHtml(input.item.slug)})</p>`,
    `<p><strong>Reason:</strong> ${escapeHtml(label)}</p>`,
    `<p><strong>From:</strong> ${escapeHtml(who)}</p>`,
    `<blockquote>${escapeHtml(input.message).replace(/\n/g, "<br>")}</blockquote>`,
    `<p><a href="${escapeHtml(link)}">Review this report</a></p>`,
  ].join("\n");
  return { subject, text: lines.join("\n"), html };
}
